import React, { useCallback, useEffect, useState } from 'react';
import { Maximize2, Minimize2, X } from 'lucide-react';

/** 넓게 · 좁게를 기억하는 키. 사람마다 화면이 달라 한 번 고르면 그대로 둔다 */
const WIDE_KEY = 'grk.sidePanel.wide';
/** 닫힐 때 밀려 나가는 시간. transition 의 duration 과 같아야 한다 */
const SLIDE_MS = 300;
/** 열린 패널 순서 — 패널 위에 패널(상세 위 수정)이 열리면 Esc 는 맨 위 것만 닫는다 */
const stack: number[] = [];
let seq = 0;

const readWide = () => {
  try {
    return localStorage.getItem(WIDE_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * GRK_project 에서 옮겼다(components/SidePanel.tsx, 2026-11-02) — 고칠 때 두 곳을 같이 본다.
 *
 * 오른쪽에서 밀려 나오는 상세 패널 — admin(CR · OPEX 상세)과 같은 모양이다. 상세는 이 패널로 연다(UI 규칙).
 *
 * 가운데 모달은 목록을 가렸다. 오른쪽 패널은 왼쪽에 목록이 비쳐 보여
 * 여러 공고를 오가며 보기 편하다(2026-10-01). 「넓게」를 누르면 화면 대부분을 쓴다 —
 * AI 요약 · 첨부가 긴 공고를 읽을 때.
 *
 * 머리 · 본문(스크롤) · 아래 셋으로 나눈다. 본문만 스크롤한다.
 *
 * investment 에서 더한 것(2026-11-02) — `bare`: 입력 창(자금원 추가 · 수정 · 기록 추가 등)이 제 머리 · 아래를 그대로 쓴다.
 * 팝업도 오른쪽 패널로 연다(UI 규칙). 패널 위 패널은 Esc 가 맨 위 것만 닫는다.
 */
export const SidePanel: React.FC<{
  onClose: () => void;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  /** 입력 창 — 패널 머리 · 아래 없이 children 이 제 머리 · 본문 · 아래를 그린다 */
  bare?: boolean;
  children: React.ReactNode;
}> = ({ onClose, header, footer, bare = false, children }) => {
  const [shown, setShown] = useState(false);
  const [wide, setWide] = useState(readWide);
  const [id] = useState(() => ++seq);
  useEffect(() => {
    stack.push(id);
    return () => { const i = stack.indexOf(id); if (i >= 0) stack.splice(i, 1); };
  }, [id]);

  /* 붙은 뒤에 켜야 밀려 나오는 게 보인다. requestAnimationFrame 은 쓰지 않는다 —
     보이지 않는 탭에서는 돌지 않아 패널이 화면 밖에 머문다(2026-10-01 staging 에서 겪음) */
  useEffect(() => {
    const id = setTimeout(() => setShown(true), 20);
    return () => clearTimeout(id);
  }, []);

  const close = useCallback(() => {
    setShown(false);
    setTimeout(onClose, SLIDE_MS);
  }, [onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && stack[stack.length - 1] === id && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close, id]);

  const toggleWide = () => {
    setWide((w) => {
      try {
        localStorage.setItem(WIDE_KEY, w ? '0' : '1');
      } catch {
        /* 저장이 막혀도 이번 화면에서는 바뀐다 */
      }
      return !w;
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div
        className={`absolute inset-0 bg-black transition-opacity duration-300 ${shown ? 'opacity-30' : 'opacity-0'}`}
        onClick={close}
      />
      <div
        className={`absolute inset-y-0 right-0 w-full ${bare ? 'max-w-xl' : wide ? 'max-w-6xl' : 'max-w-2xl'} bg-white shadow-2xl flex flex-col transform transition-all duration-300 ease-in-out ${
          shown ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {bare ? <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">{children}</div> : <>
        <header className="flex-shrink-0 border-b border-slate-200 px-6 py-4 flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">{header}</div>
          <div className="flex flex-shrink-0 items-center gap-1">
            <button
              onClick={toggleWide}
              title={wide ? '좁게' : '넓게'}
              className="hidden sm:inline-flex rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              {wide ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
            <button onClick={close} title="닫기 (Esc)" className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
              <X size={20} />
            </button>
          </div>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">{children}</div>

        {footer && <footer className="flex-shrink-0 border-t border-slate-200 px-6 py-3 flex justify-end gap-2">{footer}</footer>}
        </>}
      </div>
    </div>
  );
};
